import { useLatestUpdatedAtRef } from '@psyomics/ui-common';
import { useCallback, useRef } from 'react';
import { Model } from 'survey-react-ui';

import { useUpdateReferralRequestSurvey } from '../hooks/use-update-referral-request-survey';

type SurveyData = Record<string, unknown>;

type UseSurveyUpdateQueueProps = {
  ready: boolean;
  reviewMode: boolean;
  onComplete: (data: SurveyData) => void;
  surveyResponseId: string;
  surveyResponseUpdatedAt?: Date;
  updateSurveyAnswers: ReturnType<typeof useUpdateReferralRequestSurvey>[1];
};

const useSurveyUpdateQueue = ({
  ready,
  reviewMode,
  onComplete,
  surveyResponseId,
  surveyResponseUpdatedAt,
  updateSurveyAnswers,
}: UseSurveyUpdateQueueProps) => {
  const isUpdatingRef = useRef(false);
  const nextToUpdateRef = useRef<{ answersJson: string; complete: boolean } | null>(null);
  const latestUpdatedAtRef = useLatestUpdatedAtRef({ id: surveyResponseId, updatedAt: surveyResponseUpdatedAt });

  const runNextUpdate = useCallback(async () => {
    if (isUpdatingRef.current) return;

    const next = nextToUpdateRef.current;
    if (!next || !surveyResponseId || !ready) return;

    nextToUpdateRef.current = null;
    isUpdatingRef.current = true;

    try {
      if (!reviewMode) {
        const result = await updateSurveyAnswers({
          input: {
            id: surveyResponseId,
            answers: next.answersJson,
            complete: next.complete,
            expectedUpdatedAt: latestUpdatedAtRef.current,
          },
        });

        const updatedAt = result.data?.updateReferralRequestSurvey?.updatedAt;
        if (updatedAt) latestUpdatedAtRef.current = new Date(updatedAt);

        if (next.complete) onComplete(JSON.parse(next.answersJson));
      }
    } finally {
      isUpdatingRef.current = false;
    }

    if (nextToUpdateRef.current) void runNextUpdate();
  }, [ready, onComplete, surveyResponseId, updateSurveyAnswers, reviewMode, latestUpdatedAtRef]);

  const enqueueUpdateSurvey = useCallback(
    async (survey: Model, complete: boolean) => {
      nextToUpdateRef.current = { answersJson: JSON.stringify(survey.data), complete };
      void runNextUpdate();
    },
    [runNextUpdate]
  );

  return enqueueUpdateSurvey;
};

export { useSurveyUpdateQueue };
